import { __decorate, __extends } from "tslib";
import { PostProcess } from "./postProcess";
import { Texture } from "../Materials/Textures/texture";
import "../Shaders/kernelBlur.fragment";
import "../Shaders/kernelBlur.vertex";
import { _TypeStore } from '../Misc/typeStore';
import { serialize, serializeAsVector2, SerializationHelper } from '../Misc/decorators';
/**
 * The Blur Post Process which blurs an image based on a kernel and direction.
 * Can be used twice in x and y directions to perform a guassian blur in two passes.
 */
var BlurPostProcess = /** @class */ (function (_super) {
    __extends(BlurPostProcess, _super);
    /**
     * Creates a new instance BlurPostProcess
     * @param name The name of the effect.
     * @param direction The direction in which to blur the image.
     * @param kernel The size of the kernel to be used when computing the blur. eg. Size of 3 will blur the center pixel by 2 pixels surrounding it.
     * @param options The required width/height ratio to downsize to before computing the render pass. (Use 1.0 for full size)
     * @param camera The camera to apply the render pass to.
     * @param samplingMode The sampling mode to be used when computing the pass. (default: 0)
     * @param engine The engine which the post process will be applied. (default: current engine)
     * @param reusable If the post process can be reused on the same frame. (default: false)
     * @param textureType Type of textures used when performing the post process. (default: 0)
     * @param blockCompilation If compilation of the shader should not be done in the constructor. The updateEffect method can be used to compile the shader at a later time. (default: false)
     */
    function BlurPostProcess(name, direction, kernel, options, camera, samplingMode, engine, reusable, textureType, defines, blockCompilation) {
        if (samplingMode === void 0) { samplingMode = Texture.BILINEAR_SAMPLINGMODE; }
        if (textureType === void 0) { textureType = 0; }
        if (defines === void 0) { defines = ""; }
        if (blockCompilation === void 0) { blockCompilation = false; }
        var _this = _super.call(this, name, "kernelBlur", ["delta", "direction", "cameraMinMaxZ"], ["circleOfConfusionSampler"], options, camera, samplingMode, engine, reusable, null, textureType, "kernelBlur", { varyingCount: 0, depCount: 0 }, true) || this;
        _this.blockCompilation = blockCompilation;
        _this._packedFloat = false;
        _this._staticDefines = "";
        _this._staticDefines = defines;
        _this.direction = direction;
        _this.onApplyObservable.add(function (effect) {
            if (_this._outputTexture) {
                effect.setFloat2('delta', (1 / _this._outputTexture.width) * _this.direction.x, (1 / _this._outputTexture.height) * _this.direction.y);
            }
            else {
                effect.setFloat2('delta', (1 / _this.width) * _this.direction.x, (1 / _this.height) * _this.direction.y);
            }
        });
        _this.kernel = kernel;
        return _this;
    }
    Object.defineProperty(BlurPostProcess.prototype, "kernel", {
        /**
         * Gets the length in pixels of the blur sample region
         */
        get: function () {
            return this._idealKernel;
        },
        /**
         * Sets the length in pixels of the blur sample region
         */
        set: function (v) {
            if (this._idealKernel === v) {
                return;
            }
            v = Math.max(v, 1);
            this._idealKernel = v;
            this._kernel = this._nearestBestKernel(v);
            if (!this.blockCompilation) {
                this._updateParameters();
            }
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(BlurPostProcess.prototype, "packedFloat", {
        /**
         * Gets wether or not the blur is unpacking/repacking floats
         */
        get: function () {
            return this._packedFloat;
        },
        /**
         * Sets wether or not the blur needs to unpack/repack floats
         */
        set: function (v) {
            if (this._packedFloat === v) {
                return;
            }
            this._packedFloat = v;
            if (!this.blockCompilation) {
                this._updateParameters();
            }
        },
        enumerable: false,
        configurable: true
    });
    /**
     * Gets a string identifying the name of the class
     * @returns "BlurPostProcess" string
     */
    BlurPostProcess.prototype.getClassName = function () {
        return "BlurPostProcess";
    };
    /**
     * Updates the effect with the current post process compile time values and recompiles the shader.
     * @param defines Define statements that should be added at the beginning of the shader. (default: null)
     * @param uniforms Set of uniform variables that will be passed to the shader. (default: null)
     * @param samplers Set of Texture2D variables that will be passed to the shader. (default: null)
     * @param indexParameters The index parameters to be used for babylons include syntax "#include<kernelBlurVaryingDeclaration>[0..varyingCount]". (default: undefined) See usage in babylon.blurPostProcess.ts and kernelBlur.vertex.fx
     * @param onCompiled Called when the shader has been compiled.
     * @param onError Called if there is an error when compiling a shader.
     */
    BlurPostProcess.prototype.updateEffect = function (defines, uniforms, samplers, indexParameters, onCompiled, onError) {
        if (defines === void 0) { defines = null; }
        if (uniforms === void 0) { uniforms = null; }
        if (samplers === void 0) { samplers = null; }
        this._updateParameters(onCompiled, onError);
    };
    BlurPostProcess.prototype._updateParameters = function (onCompiled, onError) {
        // Generate sampling offsets and weights
        var N = this._kernel;
        var centerIndex = (N - 1) / 2;
        // Generate Gaussian sampling weights over kernel
        var offsets = [];
        var weights = [];
        var totalWeight = 0;
        for (var i = 0; i < N; i++) {
            var u = i / (N - 1);
            var w = this._gaussianWeight(u * 2.0 - 1);
            offsets[i] = (i - centerIndex);
            weights[i] = w;
            totalWeight += w;
        }
        // Normalize weights
        for (var i = 0; i < weights.length; i++) {
            weights[i] /= totalWeight;
        }
        // Optimize: combine samples to take advantage of hardware linear sampling
        // Walk from left to center, combining pairs (symmetrically)
        var linearSamplingWeights = [];
        var linearSamplingOffsets = [];
        var linearSamplingMap = [];
        for (var i = 0; i <= centerIndex; i += 2) {
            var j = Math.min(i + 1, Math.floor(centerIndex));
            var singleCenterSample = i === j;
            if (singleCenterSample) {
                linearSamplingMap.push({ o: offsets[i], w: weights[i] });
            }
            else {
                var sharedCell = j === centerIndex;
                var weightLinear = (weights[i] + weights[j] * (sharedCell ? .5 : 1.));
                var offsetLinear = offsets[i] + 1 / (1 + weights[i] / weights[j]);
                if (offsetLinear === 0) {
                    linearSamplingMap.push({ o: offsets[i], w: weights[i] });
                    linearSamplingMap.push({ o: offsets[i + 1], w: weights[i + 1] });
                }
                else {
                    linearSamplingMap.push({ o: offsetLinear, w: weightLinear });
                    linearSamplingMap.push({ o: -offsetLinear, w: weightLinear });
                }
            }
        }
        for (var i = 0; i < linearSamplingMap.length; i++) {
            linearSamplingOffsets[i] = linearSamplingMap[i].o;
            linearSamplingWeights[i] = linearSamplingMap[i].w;
        }
        // Replace with optimized
        offsets = linearSamplingOffsets;
        weights = linearSamplingWeights;
        // Generate shaders
        var maxVaryingRows = this.getEngine().getCaps().maxVaryingVectors;
        var freeVaryingVec2 = Math.max(maxVaryingRows, 0.) - 1; // Because of sampleCenter
        var varyingCount = Math.min(offsets.length, freeVaryingVec2);
        var defines = "";
        defines += this._staticDefines;
        // The DOF fragment should ignore the center pixel when looping as it is handled manualy in the fragment shader.
        if (this._staticDefines.indexOf("DOF") != -1) {
            defines += "#define CENTER_WEIGHT " + this._glslFloat(weights[varyingCount - 1]) + "\r\n";
            varyingCount--;
        }
        for (var i = 0; i < varyingCount; i++) {
            defines += "#define KERNEL_OFFSET" + i + " " + this._glslFloat(offsets[i]) + "\r\n";
            defines += "#define KERNEL_WEIGHT" + i + " " + this._glslFloat(weights[i]) + "\r\n";
        }
        var depCount = 0;
        for (var i = freeVaryingVec2; i < offsets.length; i++) {
            defines += "#define KERNEL_DEP_OFFSET" + depCount + " " + this._glslFloat(offsets[i]) + "\r\n";
            defines += "#define KERNEL_DEP_WEIGHT" + depCount + " " + this._glslFloat(weights[i]) + "\r\n";
            depCount++;
        }
        if (this.packedFloat) {
            defines += "#define PACKEDFLOAT 1";
        }
        this.blockCompilation = false;
        _super.prototype.updateEffect.call(this, defines, null, null, {
            varyingCount: varyingCount,
            depCount: depCount
        }, onCompiled, onError);
    };
    /**
     * Best kernels are odd numbers that when divided by 2, their integer part is even, so 5, 9 or 13.
     * Other odd kernels optimize correctly but require proportionally more samples, even kernels are
     * possible but will produce minor visual artifacts. Since each new kernel requires a new shader we
     * want to minimize kernel changes, having gaps between physical kernels is helpful in that regard.
     * The gaps between physical kernels are compensated for in the weighting of the samples
     * @param idealKernel Ideal blur kernel.
     * @return Nearest best kernel.
     */
    BlurPostProcess.prototype._nearestBestKernel = function (idealKernel) {
        var v = Math.round(idealKernel);
        for (var _i = 0, _a = [v, v - 1, v + 1, v - 2, v + 2]; _i < _a.length; _i++) {
            var k = _a[_i];
            if (((k % 2) !== 0) && ((Math.floor(k / 2) % 2) === 0) && k > 0) {
                return Math.max(k, 3);
            }
        }
        return Math.max(v, 3);
    };
    /**
     * Calculates the value of a Gaussian distribution with sigma 3 at a given point.
     * @param x The point on the Gaussian distribution to sample.
     * @return the value of the Gaussian function at x.
     */
    BlurPostProcess.prototype._gaussianWeight = function (x) {
        //reference: Engines/ImageProcessingBlur.cpp #dcc760
        // We are evaluating the Gaussian (normal) distribution over a kernel parameter space of [-1,1],
        // so we truncate at three standard deviations by setting stddev (sigma) to 1/3.
        // The choice of 3-sigma truncation is common but arbitrary, and means that the signal is
        // truncated at around 1.3% of peak strength.
        //the distribution is scaled to account for the difference between the actual kernel size and the requested kernel size
        var sigma = (1 / 3);
        var denominator = Math.sqrt(2.0 * Math.PI) * sigma;
        var exponent = -((x * x) / (2.0 * sigma * sigma));
        var weight = (1.0 / denominator) * Math.exp(exponent);
        return weight;
    };
    /**
      * Generates a string that can be used as a floating point number in GLSL.
      * @param x Value to print.
      * @param decimalFigures Number of decimal places to print the number to (excluding trailing 0s).
      * @return GLSL float string.
      */
    BlurPostProcess.prototype._glslFloat = function (x, decimalFigures) {
        if (decimalFigures === void 0) { decimalFigures = 8; }
        return x.toFixed(decimalFigures).replace(/0+$/, '');
    };
    /** @hidden */
    BlurPostProcess._Parse = function (parsedPostProcess, targetCamera, scene, rootUrl) {
        return SerializationHelper.Parse(function () {
            return new BlurPostProcess(parsedPostProcess.name, parsedPostProcess.direction, parsedPostProcess.kernel, parsedPostProcess.options, targetCamera, parsedPostProcess.renderTargetSamplingMode, scene.getEngine(), parsedPostProcess.reusable, parsedPostProcess.textureType, undefined, false);
        }, parsedPostProcess, scene, rootUrl);
    };
    __decorate([
        serialize("kernel")
    ], BlurPostProcess.prototype, "_kernel", void 0);
    __decorate([
        serialize("packedFloat")
    ], BlurPostProcess.prototype, "_packedFloat", void 0);
    __decorate([
        serializeAsVector2()
    ], BlurPostProcess.prototype, "direction", void 0);
    return BlurPostProcess;
}(PostProcess));
export { BlurPostProcess };
_TypeStore.RegisteredTypes["BABYLON.BlurPostProcess"] = BlurPostProcess;
//# sourceMappingURL=blurPostProcess.js.map